import { Store } from '@ngrx/store';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import { ReplaySubject, takeUntil } from 'rxjs';
import * as handPoseDetection from '@tensorflow-models/hand-pose-detection';
import fp from 'fingerpose';
import { EmoteService } from './emote/emote.service';
import { Injectable, inject } from '@angular/core';

interface Keypoint3D {
  x: number;
  y: number;
  z: number;
}

const MINIMUM_CONFIDENCE = 9;

@Injectable({
  providedIn: 'root',
})
export class HandposeDetectionService extends Loggable {
  private store = inject(Store);
  private emoteService = inject(EmoteService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private gestureDetection = false;
  private handPoseModel?: handPoseDetection.HandDetector;
  private gestureEstimator: fp.GestureEstimator;
  private detectedTimes = 0;

  public constructor() {
    super();
    this.setDisplayName('HandposeDetectionService');

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.gesture_detection)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((gesture_detection) => {
        this.gestureDetection = gesture_detection;
      });

    this.gestureEstimator = new fp.GestureEstimator([
      fp.Gestures.ThumbsUpGesture,
      fp.Gestures.VictoryGesture,
      ...this.customGestures(),
    ]);
  }

  public async initHandpose(video: HTMLVideoElement): Promise<void> {
    this.info('Initializing handpose detection...');

    if (!this.gestureDetection) {
      this.info('Handpose detection is disabled');
      return;
    }

    if (this.handPoseModel) {
      this.info('Hand pose model is already initialized');
      return;
    }

    const detectorConfig = {
      runtime: 'mediapipe',
      modelType: 'full',
      maxHands: 2,
      solutionPath: `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1646424915`,
      minDetectionConfidence: 0.9,
    };

    this.handPoseModel = await handPoseDetection.createDetector(
      handPoseDetection.SupportedModels.MediaPipeHands,
      detectorConfig as unknown as handPoseDetection.MediaPipeHandsTfjsModelConfig,
    );

    if (!this.handPoseModel) {
      this.error('Failed to load hand pose model');
      return;
    }

    this.info('Hand pose model loaded successfully');
    void this.detectHands(video);
  }

  private async detectHands(video: HTMLVideoElement) {
    if (!this.gestureDetection) {
      this.info('Handpose detection is disabled');
      return;
    }

    if (!this.handPoseModel) {
      this.info('Hand pose model is not initialized');
      return;
    }

    // get hand landmarks from video
    const hands = await this.handPoseModel.estimateHands(video, {
      flipHorizontal: true,
    });

    for (const hand of hands) {
      if (!hand.keypoints3D) {
        this.info('No keypoints3D found');
        return;
      }

      this.info('Hand: ', hand);

      const keypoints3D = hand.keypoints3D.map((keypoint) => [
        keypoint.x,
        keypoint.y,
        keypoint.z,
      ]) as unknown as Keypoint3D[];
      const predictions = this.gestureEstimator.estimate(keypoints3D, MINIMUM_CONFIDENCE);

      if (predictions.gestures.length) {
        const result = predictions.gestures.reduce((p, c) => (p.score > c.score ? p : c));

        const found = result.name;

        this.info('Gesture detected:', found);

        if (!found) {
          this.info('No gesture detected');
          this.detectedTimes = 0;
          continue;
        }

        if (found) {
          this.detectedTimes++;
        }

        if (this.detectedTimes === 2) {
          this.sendEmote(found);
        }
      }
    }
    setTimeout(() => {
      void this.detectHands(video);
    }, 1000);
  }

  private sendEmote(emote: string): void {
    setTimeout(() => {
      this.detectedTimes = 0;
    }, 3000);

    switch (emote) {
      case 'thumbs_up':
        this.emoteService.setReactionMessage({
          text: 'thumbsup',
          id: 'thumbsup',
          name: 'thumbsup',
          colons: ':thumbsup:',
          emoticons: ['👍'],
          skin: null,
          native: '👍',
        });
        break;
      case 'victory':
        this.emoteService.setReactionMessage({
          text: 'victory',
          id: 'victory',
          name: 'victory',
          colons: ':victory:',
          emoticons: ['✌️'],
          skin: null,
          native: '✌️',
        });
        break;
      case 'rocknroll':
        this.emoteService.setReactionMessage({
          text: 'rocknroll',
          id: 'rocknroll',
          name: 'rocknroll',
          colons: ':rocknroll:',
          emoticons: ['🤘'],
          skin: null,
          native: '🤘',
        });
    }
  }

  private customGestures() {
    const { Finger, FingerCurl } = fp;
    const rocknroll = new fp.GestureDescription('rocknroll');

    rocknroll.addCurl(Finger.Thumb, FingerCurl.NoCurl, 1.0);
    rocknroll.addCurl(Finger.Index, FingerCurl.NoCurl, 1.0);
    rocknroll.addCurl(Finger.Middle, FingerCurl.FullCurl, 1.0);
    rocknroll.addCurl(Finger.Ring, FingerCurl.FullCurl, 1.0);
    rocknroll.addCurl(Finger.Pinky, FingerCurl.NoCurl, 1.0);

    return [rocknroll];
  }
}
